/* eslint eslint-comments/no-use: off */
import {formatBlobRange} from '@github-ui/blob-anchor'
import {useEditPermissions} from '@github-ui/code-view-shared/contexts/EditPermissionsContext'
import {usePath} from '@github-ui/code-view-shared/contexts/PathContext'
import {useRefInfo} from '@github-ui/code-view-shared/contexts/RefInfoContext'
import {useCurrentRepository} from '@github-ui/current-repository'
import {editBlobPath} from '@github-ui/paths'
import {Link} from '@github-ui/react-core/link'
// useLocation is safe for files not rendered in a partial on the overview.
// eslint-disable-next-line no-restricted-imports
import {useLocation} from '@github-ui/react-router'
import {clsx} from 'clsx'
import {useEffect, useImperativeHandle, useRef, useState} from 'react'

import {useCurrentLineHeight} from '../../../../hooks/use-current-line-height'
import {minLeftOffsetBlame, minLeftOffsetBlob, useCursorNavigation} from '../../../../hooks/use-cursor-navigation'
import type {SetStickyLinesType} from '../../../../hooks/use-sticky-lines'
import {getActualLineNumberBinarySearch} from '../../../../utilities/lines'
import type {CodeNavData} from '../BlobContent'
import HighlightedLineMenu, {firstOptionId, type HighlightedLineMenuHandle} from '../HighlightedLineMenu'
import type {CodeLineData} from './hooks/use-code-lines'
import styles from './NavigationCursor.module.css'

// UI Commands need to be scoped to the textarea with `ScopedCommands`, which requires the commands to be rendered in
// the parent and then 'reach down' into the cursor for navigation. This should eventually be refactored to lift up the
// cursor nav logic, or have this component also render the textarea.
export interface NavigationCursorHandle {
  inspectSymbol: () => void
  updateUrlForLineNumber: () => void
  showLineMenu: () => void
  onPageDown: () => void
  onPageUp: () => void
}

export function NavigationCursor({
  linesData,
  onCodeNavTokenSelected,
  onLineNumberClick,
  isBlame,
  isCursorVisible,
  isVirtualized,
  textAreaRef,
  shouldRenderOverlay,
  tabSize,
  optionalTestLeftOffsetFunction,
  textSelection,
  onCollapseToggle,
  onLineStickOrUnstick,
  optionalTestTopOffsetFunction,
  additionalTextAreaInstructions,
  ref,
}: {
  linesData: readonly CodeLineData[]
  onCodeNavTokenSelected?: (symbol: CodeNavData) => void
  cursorClickPosition?: {startX: number; startY: number; endX: number; endY: number} | undefined
  onLineNumberClick?: (event?: React.MouseEvent) => void
  isBlame: boolean
  isCursorVisible: boolean
  isVirtualized: boolean
  textAreaRef: React.RefObject<HTMLTextAreaElement | null> | undefined
  shouldRenderOverlay: boolean
  tabSize: number
  textSelection?: {start: number; end: number; keyboard: boolean; displayStart: boolean}
  additionalTextAreaInstructions: string
  onCollapseToggle: () => void
  onLineStickOrUnstick?: SetStickyLinesType
  optionalTestLeftOffsetFunction?: (leftOffset: number) => void
  optionalTestTopOffsetFunction?: (leftOffset: number) => void
  ref?: React.Ref<NavigationCursorHandle>
}) {
  const [leftOffset, setLeftOffset] = useState(0)
  const [topOffset, setTopOffset] = useState(0)
  const [shouldShowHighlightMenu, setShouldShowHighlightMenu] = useState(false)
  const lastKeyPressedRef = useRef('')
  const menuRef = useRef<HighlightedLineMenuHandle>(null)
  const repo = useCurrentRepository()
  const path = usePath()
  const refInfo = useRefInfo()
  const {canEdit} = useEditPermissions()

  //TODO: implement wrapping
  //const wrapOption = useCodeViewOptions().codeWrappingOption

  //wasExpandCollapseCodeSection will default to false for every call except the call from expand and collapse
  function onLineMenuClose(value: boolean, wasExpandCollapseCodeSection: boolean) {
    setShouldShowHighlightMenu(value)
    setTimeout(() => {
      //only if it was an expand/collapse event do we want to set the text area cursor position to be kinda near where
      //it should be. It's not exact, but I don't really think it's worth worrying over, becuase the alternative is
      //that the cursor is at the very end of the file (because the contents of the text area changed, that is the
      //default behavior)
      if (wasExpandCollapseCodeSection) determineAndSetTextAreaCursorPosition()
      textAreaRef?.current?.focus()
      //300ms timeout so that there is enough time for screen readers to read off their stuff before text area
      //screen reading takes over
    }, 300)
  }

  function showLineMenu(event?: React.MouseEvent<HTMLButtonElement>) {
    if (event) {
      event.preventDefault()
      event.stopPropagation()
    }
    setShouldShowHighlightMenu(true)
    setTimeout(() => {
      //need to do it with a timeout so that the line menu is rendered when we are setting the anchor
      menuRef.current?.setAnchor(cursorRef.current)
    }, 0)
    return false
  }

  const location = useLocation()
  useEffect(() => {
    setLeftOffset(0)
    setTopOffset(0)
  }, [location.key])

  useEffect(() => {
    function keydownKeyChecking(event: KeyboardEvent) {
      // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
      lastKeyPressedRef.current = event.key
    }
    function contextMenuCheckDoNotTriggerBasicMenu(event: MouseEvent) {
      if (
        lastKeyPressedRef.current === 'ContextMenu' &&
        //-1 means it was the contextmenu hotkey button
        event.button === -1 &&
        (document.activeElement as Element)?.className.indexOf(firstOptionId) !== -1
      ) {
        lastKeyPressedRef.current = ''
        event?.preventDefault()
        event?.stopPropagation()
        return false
      }
    }

    window.oncontextmenu = contextMenuCheckDoNotTriggerBasicMenu
    window.addEventListener('keydown', keydownKeyChecking)

    return () => {
      window.removeEventListener('keydown', keydownKeyChecking)
      //setting the context menu to null resets it to just be the browser default
      window.oncontextmenu = null
    }
  }, [])

  const minLeftOffset = isBlame ? minLeftOffsetBlame : minLeftOffsetBlob
  const cursorRef = useRef<HTMLDivElement>(null)

  const {
    inspectSymbol,
    updateUrlForLineNumber,
    onPageUp,
    onPageDown,
    currentStartLine,
    currentStartChar,
    currentEndLine,
    currentEndChar,
    determineAndSetTextAreaCursorPosition,
    getCorrectLineNumberWithCollapsedSections,
  } = useCursorNavigation(
    cursorRef,
    onCodeNavTokenSelected,
    optionalTestLeftOffsetFunction ?? setLeftOffset,
    optionalTestTopOffsetFunction ?? setTopOffset,
    linesData,
    isVirtualized,
    isBlame,
    onLineNumberClick,
    textAreaRef,
    tabSize,
    additionalTextAreaInstructions,
    textSelection,
  )
  const isMobileRef = useRef(false)

  function determineClosestParentCodeSection() {
    if (currentStartLine.current !== currentEndLine.current) {
      return null
    }
    const currentLineData = linesData[currentStartLine.current]
    if (!currentLineData) return null
    //if the current line is a start line, we don't have to do any extra work
    if (currentLineData.isStartLine) return currentLineData
    if (currentLineData.codeLineClassName === '') {
      //return null because there isn't a code section for this line
      return null
    }
    const lineCodeSections = currentLineData.codeLineClassName?.split('child-of-line-')
    if (!lineCodeSections || currentLineData.codeLineClassName?.indexOf('child-of-line-') === -1) {
      return null
    }
    //making this undefined if it can't find the lineCodeSection so that it doesn't pass the isNaN check
    const closestParentCodeSection = parseInt(lineCodeSections[lineCodeSections.length - 1]?.trim() ?? 'undefined')
    if (closestParentCodeSection && !Number.isNaN(closestParentCodeSection)) {
      if (
        linesData[closestParentCodeSection - 1] &&
        linesData[closestParentCodeSection - 1]?.lineNumber === closestParentCodeSection
      ) {
        //save the time of doing the binary search
        return linesData[closestParentCodeSection - 1]
      }
      //if any lines have been collapsed, we need to actually find the index in the linesData array
      //where the closestParentCodeSection is
      return linesData[getActualLineNumberBinarySearch(closestParentCodeSection, linesData)]
    }

    return null
  }

  useEffect(() => {
    isMobileRef.current = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent)
  }, [])

  const lineHeight = useCurrentLineHeight('react-line-numbers')
  const cursorStyle: React.CSSProperties | undefined = isCursorVisible
    ? {
        height: `${lineHeight}px`,
        // eslint-disable-next-line react-hooks/refs
        visibility: isMobileRef.current ? 'hidden' : 'visible',
      }
    : undefined

  let editorLinkHash
  if (currentStartLine.current === currentEndLine.current && currentStartChar.current === currentEndChar.current) {
    editorLinkHash = `#L${currentStartLine.current + 1}C${currentStartChar.current}`
  } else {
    editorLinkHash = `#${formatBlobRange({
      start: {line: currentStartLine.current + 1, column: currentStartChar.current},
      end: {line: currentEndLine.current + 1, column: currentEndChar.current},
    })}`
  }

  useImperativeHandle(ref, () => ({
    inspectSymbol,
    updateUrlForLineNumber,
    showLineMenu,
    onPageDown,
    onPageUp,
  }))

  return (
    <>
      <div
        aria-hidden
        style={{
          top: topOffset,
          left: minLeftOffset + leftOffset,
          ...cursorStyle,
        }}
        ref={cursorRef}
        data-testid="navigation-cursor"
        className={clsx('code-navigation-cursor', isCursorVisible && styles.navigationCursor)}
      >
        {' '}
      </div>
      {shouldRenderOverlay && (
        <div
          style={{
            top: topOffset + lineHeight,
            left: minLeftOffset + leftOffset,
            zIndex: 2,
          }}
          className="position-absolute border rounded-2 color-bg-subtle tmp-px-3 py-2"
        >
          <span style={{pointerEvents: 'auto'}}>
            Code view is read-only.{' '}
            {canEdit && (
              <Link
                to={editBlobPath({
                  owner: repo.ownerLogin,
                  repo: repo.name,
                  filePath: path,
                  commitish: refInfo.name,
                  hash: editorLinkHash,
                })}
              >
                Switch to the editor.
              </Link>
            )}
          </span>
        </div>
      )}
      {shouldShowHighlightMenu && (
        <HighlightedLineMenu
          ref={menuRef}
          rowBeginId={`LG${getCorrectLineNumberWithCollapsedSections(currentStartLine.current)}`}
          rowBeginNumber={getCorrectLineNumberWithCollapsedSections(currentStartLine.current)}
          rowEndNumber={getCorrectLineNumberWithCollapsedSections(currentEndLine.current)}
          rowEndId={`LG${getCorrectLineNumberWithCollapsedSections(currentEndLine.current)}`}
          openOnLoad
          cursorRef={cursorRef}
          onCollapseToggle={onCollapseToggle}
          onLineStickOrUnstick={onLineStickOrUnstick}
          lineData={determineClosestParentCodeSection()}
          onMenuClose={onLineMenuClose}
        />
      )}
    </>
  )
}
